// Forgot Password Page - Direct Reset Link (No OTP)
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from '../hooks/use-toast';
import { useLanguage } from '../i18n/LanguageContext';
import { Mail, Lock, ArrowLeft, Check, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1); // 1: email, 2: new password (if token exists)
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  
  // Check if there's a reset token in URL
  const resetToken = searchParams.get('token');
  
  useEffect(() => {
    if (resetToken) {
      setStep(2);
    }
  }, [resetToken]);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send reset link');
      }

      setEmailSent(true);
      toast({
        title: isRTL ? 'تم الإرسال' : 'Sent',
        description: isRTL ? 'تم إرسال رابط إعادة تعيين كلمة المرور إلى بريدك' : 'Password reset link sent to your email',
      });
    } catch (error) {
      toast({
        title: isRTL ? 'خطأ' : 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast({
        title: isRTL ? 'خطأ' : 'Error',
        description: isRTL ? 'كلمات المرور غير متطابقة' : 'Passwords do not match',
        variant: 'destructive'
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: isRTL ? 'خطأ' : 'Error',
        description: isRTL ? 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' : 'Password must be at least 6 characters',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          reset_token: resetToken,
          new_password: newPassword 
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to reset password');
      }

      toast({
        title: isRTL ? 'تم بنجاح' : 'Success',
        description: isRTL ? 'تم تغيير كلمة المرور بنجاح' : 'Password changed successfully',
      });

      navigate('/');
    } catch (error) {
      toast({
        title: isRTL ? 'خطأ' : 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Email sent confirmation screen
  if (emailSent && step === 1) {
    return (
      <div className="min-h-screen bg-[#F7F1E8] flex items-center justify-center p-4" dir={isRTL ? 'rtl' : 'ltr'}>
        <Card className="w-full max-w-md bg-[#FFFDF8] border-[#E6DCC8]">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-500" />
            </div>
            <CardTitle className="text-2xl text-[#1B3D2F]">
              {isRTL ? 'تم إرسال الرابط' : 'Link Sent'}
            </CardTitle>
            <CardDescription className="text-[#6B5E4A]">
              {isRTL 
                ? 'تم إرسال رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني. يرجى التحقق من صندوق الوارد.'
                : 'A password reset link has been sent to your email. Please check your inbox.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => navigate('/')} 
              className="w-full bg-[#1B3D2F] hover:bg-[#0F241C]"
            >
              {isRTL ? 'العودة لتسجيل الدخول' : 'Back to Login'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Reset password form (when token exists)
  if (step === 2) {
    return (
      <div className="min-h-screen bg-[#F7F1E8] flex items-center justify-center p-4" dir={isRTL ? 'rtl' : 'ltr'}>
        <Card className="w-full max-w-md bg-[#FFFDF8] border-[#E6DCC8]">
          <CardHeader>
            <CardTitle className="text-2xl text-[#1B3D2F] text-center">
              {isRTL ? 'إنشاء كلمة مرور جديدة' : 'Create New Password'}
            </CardTitle>
            <CardDescription className="text-[#6B5E4A] text-center">
              {isRTL ? 'أدخل كلمة المرور الجديدة' : 'Enter your new password'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[#1B3D2F]">
                  {isRTL ? 'كلمة المرور الجديدة' : 'New Password'}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-[#8A7B66]" />
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10 bg-[#F7F1E8] border-[#E6DCC8] text-[#1B3D2F]"
                    placeholder="••••••"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#1B3D2F]">
                  {isRTL ? 'تأكيد كلمة المرور' : 'Confirm Password'}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-[#8A7B66]" />
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 bg-[#F7F1E8] border-[#E6DCC8] text-[#1B3D2F]"
                    placeholder="••••••"
                    required
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#1B3D2F] hover:bg-[#0F241C]"
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  isRTL ? 'تغيير كلمة المرور' : 'Change Password'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Request reset form
  return (
    <div className="min-h-screen bg-[#F7F1E8] flex items-center justify-center p-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <Card className="w-full max-w-md bg-[#FFFDF8] border-[#E6DCC8]">
        <CardHeader>
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#6B5E4A] hover:text-[#C6A15B] mb-4"
          >
            <ArrowLeft size={20} />
            {isRTL ? 'رجوع' : 'Back'}
          </button>
          <CardTitle className="text-2xl text-[#1B3D2F] text-center">
            {isRTL ? 'نسيت كلمة المرور؟' : 'Forgot Password?'}
          </CardTitle>
          <CardDescription className="text-[#6B5E4A] text-center">
            {isRTL 
              ? 'أدخل بريدك الإلكتروني وسنرسل لك رابط لإعادة تعيين كلمة المرور'
              : 'Enter your email and we will send you a link to reset your password'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleRequestReset} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#1B3D2F]">
                {isRTL ? 'البريد الإلكتروني' : 'Email'}
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-[#8A7B66]" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-[#F7F1E8] border-[#E6DCC8] text-[#1B3D2F]"
                  placeholder="example@email.com"
                  required
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-[#1B3D2F] hover:bg-[#0F241C]"
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                isRTL ? 'إرسال رابط إعادة التعيين' : 'Send Reset Link'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
